import { useState } from 'react';
import { toast } from 'sonner';
import { send } from '../../api';
import { Button } from '../ui/button';
import type { EmailAutonomy } from '../../../../src/domain/policy/autonomy';
import './autonomy-switch.css';
import { useAgentName } from '../../agent-name';

/** One line per mode, in the configured agent's name. */
export function autonomyLine(mode: EmailAutonomy, agent: string): string {
  return {
    approve: 'You approve every email before it goes out.',
    autonomous: `${agent} sends routine reminders within this policy without waiting for you. Disputes, unclear replies and money moves still come to you.`,
    mixed: `${agent} approves some routine emails; the rest wait for you.`,
  }[mode];
}

/**
 * Who approves routine emails: you or Amy. Owners switch it (a new policy version; money moves keep their person);
 * everyone else sees the current answer. Saving announces a state change, so Home and Needs you refetch on their own.
 */
export function AutonomySwitch({ mode, owner, policyId = 'aiprise', compact = false, onChanged }: {
  mode: EmailAutonomy; owner: boolean; policyId?: string; compact?: boolean; onChanged?: (view: unknown) => void;
}) {
  const [busy, setBusy] = useState(false);
  const agent = useAgentName();
  const names: Record<EmailAutonomy, string> = { approve: 'You', autonomous: agent, mixed: 'Mixed' };
  async function choose(next: 'approve' | 'autonomous') {
    if (next === mode || busy) return;
    setBusy(true);
    try {
      const view = await send<unknown>(`/app-api/policies/${policyId}/autonomy`, 'PUT', { mode: next });
      toast.success(next === 'autonomous' ? `${agent} now approves routine emails.` : 'You now approve every email.');
      onChanged?.(view);
    } catch (e) { toast.error(e instanceof Error ? e.message : 'This could not be changed.'); }
    finally { setBusy(false); }
  }
  const label = 'Who approves routine emails';
  return <section className={compact ? 'autonomy-switch is-compact' : 'autonomy-switch'} aria-label={label}>
    <div className="autonomy-row">
      <span className="autonomy-label">{label}</span>
      {owner && mode !== 'mixed'
        ? <div className="autonomy-segments" role="radiogroup" aria-label={label}>
          {(['approve', 'autonomous'] as const).map(option => <button key={option} type="button" role="radio"
            aria-checked={mode === option} disabled={busy} onClick={() => void choose(option)}>{names[option]}</button>)}
        </div>
        : <strong className="autonomy-value">{names[mode]}</strong>}
      {owner && mode === 'mixed' && <span className="autonomy-mixed-actions">
        <Button size="sm" variant="outline" disabled={busy} onClick={() => void choose('autonomous')}>Make it {agent}</Button>
        <Button size="sm" variant="outline" disabled={busy} onClick={() => void choose('approve')}>Make it you</Button>
      </span>}
    </div>
    {!compact && <p className="autonomy-line">{autonomyLine(mode, agent)}</p>}
  </section>;
}
